// Session state. Supabase persists the session itself; this just exposes it to React and
// keeps profiles.last_seen_at current, which is what the Днес "ново от последното" strip
// compares against (04 §1).

import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';

export interface SessionState {
  session: Session | null;
  loading: boolean;
}

const DEMO_SESSION = {
  user: {
    id: '00000000-0000-0000-0000-000000000001',
    email: 'laizyload@gmail.com',
    app_metadata: {},
    user_metadata: { name: 'Owner' },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  },
  access_token: 'demo-token',
  token_type: 'bearer',
  expires_in: 3600,
  refresh_token: 'demo-refresh',
} as unknown as Session;

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(() => {
    if (!isConfigured || isDemoMode) {
      return DEMO_SESSION;
    }
    return null;
  });
  const [loading, setLoading] = useState(!isDemoMode && isConfigured);

  useEffect(() => {
    if (!isConfigured || isDemoMode) {
      setSession(DEMO_SESSION);
      setLoading(false);
      return;
    }

    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  // Stamped once per session rather than per navigation
  useEffect(() => {
    const userId = session?.user.id;
    if (userId === undefined || !isConfigured || isDemoMode) return;
    void supabase
      .from('profiles')
      .update({ last_seen_at: new Date().toISOString() })
      .eq('user_id', userId);
  }, [session?.user.id]);

  return { session, loading };
}

export async function signInWithGoogle(): Promise<void> {
  if (!isConfigured) {
    localStorage.setItem('leadg_demo_mode', 'true');
    window.location.reload();
    return;
  }
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
}

export async function signOut(): Promise<void> {
  if (isDemoMode) {
    localStorage.removeItem('leadg_demo_mode');
    window.location.reload();
    return;
  }
  await supabase.auth.signOut();
}
